import "./UserAccount.scss";

import { useAtom } from "jotai";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

import { SlAccount } from "@/assets/icons/icons.tsx";
import { Tablist } from "@/components/Interface/Tablist/Tablist";
import { TabLayout } from "@/components/Layout/BaseLayouts/TabLayout";
import { useIsInSecuritySubFlow } from "@/hooks/useIsInSecuritySubFlow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { usePaymentsTabLabel } from "@/hooks/usePaymentsTabLabel";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useGetCustomer } from "@/services/api";
import {
  userAccountStateAtom,
  userAccountStateAtomInitialState,
} from "@/utils/atoms";

export const UserAccountHeaderCard: FC = () => {
  const { t } = useTranslation();
  const { data: customer } = useGetCustomer();

  return (
    <div className="flex flex-col gap-6 rounded-lg bg-surface-accent-ivory p-4 tablet:p-6">
      {/* Tablet/desktop align the icon tile and the title/customer-number block
          to the bottom edge of the header card, so everything sits flush along
          the container floor. */}
      <div className="flex flex-col items-start gap-4 tablet:flex-row tablet:items-end tablet:gap-6">
        <div className="flex size-24 shrink-0 items-center justify-center self-end rounded-lg bg-surface-primary-inverse tablet:self-auto">
          <SlAccount className="size-14" />
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <h1
            data-testid="tile-title"
            className="break-words text-headline-xl font-semibold leading-xs text-foreground-primary"
          >
            {t("portal:user-account.title")}
          </h1>
          <div className="inline-flex w-fit items-center gap-1 rounded-full bg-surface-accent-mint px-2 py-1">
            <span className="text-body-xs font-semibold text-foreground-primary">
              {t("Footer.customerNumber")} {customer?.customerId}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UserAccount: FC = () => {
  const [mode] = useQueryParam("mode");

  const { t } = useTranslation();

  const [userAccountState, setUserAccountState] = useAtom(userAccountStateAtom);
  const { isInvoice } = usePaymentMode();
  const paymentsTabLabel = usePaymentsTabLabel();

  // Reset the userAccountState when the component mounts or unmounts
  useEffect(() => {
    setUserAccountState(userAccountStateAtomInitialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `editingPayment` is a global atom that isn't reset on navigation. The
  // invoice gate must match PaymentMethods so a leftover flag cannot lock tabs
  // or open the chromeless mobile shell with nothing to edit.
  const isEditingPayment = userAccountState.editingPayment && !isInvoice;

  const isEditingPersonal =
    userAccountState.editingPersonalData ||
    userAccountState.editingMail ||
    userAccountState.editingPassword ||
    isEditingPayment;

  const isChangingInboxSetting = mode === "edit";

  const isInSecurityChange = useIsInSecuritySubFlow();
  const isEditingSecurity =
    userAccountState.editingSecurity || isInSecurityChange;

  // While ANY edit is open (personal data, login data, inbox settings, or a 2FA
  // sub-flow) every tab is locked, so the user can't navigate away mid-edit and
  // silently lose their input -- including the tab they're currently on.
  const isEditing =
    isEditingPersonal || isChangingInboxSetting || isEditingSecurity;

  // On mobile a security sub-flow (2FA setup/change) is a standalone screen
  // showing only the active card, so the account header and the tab strip are
  // hidden. On tablet/desktop the flow expands inline and they stay.
  // Editing personal data / Email / Password / inbox language / payment
  // method counts too: on mobile the edit card fills the
  // screen with a pinned title bar, so nothing above it may stay — same
  // in-place hide Documents uses for bill multi-select.
  const isMobile = useIsMobile();
  const isStandaloneMobileFlow =
    isMobile &&
    (isInSecurityChange ||
      userAccountState.editingPersonalData ||
      userAccountState.editingMail ||
      userAccountState.editingPassword ||
      isEditingPayment ||
      isChangingInboxSetting);

  return (
    <TabLayout
      hideChrome={isStandaloneMobileFlow}
      header={<UserAccountHeaderCard />}
      tabs={
        <Tablist
          ariaLabel={t("portal:user-account.title")}
          tabs={[
            {
              target: "personal",
              disabled: isEditing,
              text: t("Common.label.personalDetails"),
            },
            {
              target: "payments",
              disabled: isEditing,
              text: paymentsTabLabel,
            },
            {
              target: "security",
              disabled: isEditing,
              text: t("portal:user-account.tabs.security"),
            },
            {
              target: `inbox`,
              disabled: isEditing,
              text: t("portal:user-account.tabs.inbox"),
            },
          ]}
        />
      }
    >
      <div id="useraccount-container">
        <Outlet />
      </div>
    </TabLayout>
  );
};
